import { BusinessException, EntityModificationPack, FormulaEngineInstance, Measure, Vision, VisionStep } from '@wizeoo/akm-message-lib';
import { Model } from '../../model/model';
import { MeasureEngine as BaseMeasureEngine, BaselineEngine, EM_ACTION, INJECTION, MeasMode, MeasureCoreEngine, MeasureVO, VisionVO, WizConstants } from '@wizeoo/akm-compute-lib';
import { TranslatableLabelVO } from '../../vo/common/translation/translatable-label.vo';
import { WizIcon, WizPopUp, WizTranslationManager } from '@wizeoo/akm-component-lib';
import { FreedataValueVO } from '../../vo/core/freedatavalue.vo';
import { ViewData } from '../../model/view.data';
import { cloneDeep } from 'lodash-es';
import { FeiVO } from '../../vo/workingdata/fei.vo';
import { EMEngine } from '../entities/entity-modification.engine';
import { CommitCmd } from '../../command/em/commit.cmd';
import { WIZ_COMMAND_STATE } from '@wizeoo/akm-core-lib';

/**
 * params pour configuration des mesures
 */
export interface MeasureParams {
  measure: MeasureVO,
  mode: string,
  model: Model,
  callback?: Function
}

/**
 * gestion des measures pour le moteur
 * Contient toutes les measures chargées pour le périmètre de l'utilisateur
 */
export class MeasureEngine extends BaseMeasureEngine{

  constructor(model: Model) {
      super(model);
      BaselineEngine.injectionMap.set(INJECTION.MEASURE_LABEL, this.getMeasureLabel.bind(this));
      BaselineEngine.injectionMap.set(INJECTION.MEASURE_DESC, this.getMeasureDescription.bind(this));
      BaselineEngine.injectionMap.set(INJECTION.MEASURE_CONTROL_MESSAGE, this.getMeasureControlMessage.bind(this));
      BaselineEngine.injectionMap.set(INJECTION.MEASURE_EDIT_RESTRICT_MESSAGE, this.getMeasureEditRestrictMessage.bind(this));
      BaselineEngine.injectionMap.set(INJECTION.MEASURE_SELECTED, this.getMeasureIsSelected.bind(this));
      BaselineEngine.injectionMap.set(INJECTION.MEASURE_NO_DEPENDENCY, this.getMeasureNoDependency.bind(this));
  }

  protected get model(): Model {
    return this._model as Model;
  }
  protected set model(value: Model) {
      this._model = value;
  }

  /** création d'une nouvelle mesure */
  public buildFake(vision?: VisionVO): MeasureVO {

    if (!vision) { // pour les fei, styles, conditionRenderer et FormulaCore
      let proto: Vision = new Vision();
      proto.domain = this.model.domainCode;
      proto.businessUnitCode = this.model.businessUnitCode;
      proto.code = this.model.visionCode;
      proto.id = null;

      vision = this.model.visionEngine.newVisionVO(proto);

    }

    const result: MeasureVO = new MeasureVO();
    result.domain = vision.domain;
    result.businessUnitCode = vision.businessUnitCode;
    result.vision = vision.code;
    result.vid = vision.id;
    result.stepLevel = 0; // *, mesure à la vision par défaut
    result.mode = '';
    result.formulaF = '';
    result.formulaI = '';
    result.formulaS = '';
    result.formulaT = '';
    result.editable = true; // editable par défaut
    
    // on mémorise la mesure en cours
    this.model.currentMeasure = result;
    
    return result;
  }

  /**
   * retourne le label pour une mesure
   * @param measure 
   * @returns le label
   */
  public getMeasureLabel(measure : Measure) : string {
    if(!measure) {
      return '';
    }
    
    if(!!measure.label)
     {
      return this.model.localeEngine.translate(measure.label, measure.shortCode).trim();
    } else { // pas de label sur la mesure, il faut prendre le code du FDV

      let parts: string[]= measure.code.split(ViewData.CONTEXT_SEPARATOR);
      let fdvvo: FreedataValueVO = this.model.freedataEngine.getFreedataValue(parts[0], parts[1]);
      return fdvvo?.trsLabel;
    }
  }

  /**
   * retourne la description pour une mesure
   * @param measure 
   * @returns le label
   */
  public getMeasureDescription(measure : Measure) : string {
    if(!measure) {
      return '';
    }
    if(!!measure.description)
     {
      return this.model.localeEngine.translate(measure.description, measure.shortCode).trim();
    } else { // pas de label sur la mesure, il faut prendre le code du FDV

      let parts: string[]= measure.code.split(ViewData.CONTEXT_SEPARATOR);
      let fdvvo: FreedataValueVO = this.model.freedataEngine.getFreedataValue(parts[0], parts[1]);
      return fdvvo?.trsDescription;
    }
  }

  /**
   * Return msgError sur contrôle de cohérence
   * @return string
   */
  public getMeasureControlMessage(measure: Measure): string {
    return this.model.localeEngine.translate(measure.valueControlMessage, WizTranslationManager.getInstance().translateKey('$.common.error.$'));
  }

  /**
   * Return msgError sur restriction en édition
   * @return string
   */
  public getMeasureEditRestrictMessage(measure: Measure): string {
    return this.model.localeEngine.translate(measure.editRestrictMessage, WizTranslationManager.getInstance().translateKey('$.common.error.$'));
  }

  // ######### gestion des traduction

  public getMeasureLabelVO(measure: MeasureVO): TranslatableLabelVO {
    let result:  TranslatableLabelVO = this.model.localeEngine.newTranslatableVO(measure, WizConstants.FIELD_LABEL);
    return result;
  }

  public getMeasureDescriptionVO(measure: MeasureVO): TranslatableLabelVO {
    let result:  TranslatableLabelVO = this.model.localeEngine.newTranslatableVO(measure, WizConstants.FIELD_DESCRIPTION);
    return result;
  }
  
  public getMeasureEditRestrictMessageVO(measure: MeasureVO): TranslatableLabelVO {
      let result:  TranslatableLabelVO = this.model.localeEngine.newTranslatableVO(measure, 'editRestrictMessage');
    return result;
  }

  public getMeasureValueControlMessageVO(measure: MeasureVO): TranslatableLabelVO {
    let result:  TranslatableLabelVO = this.model.localeEngine.newTranslatableVO(measure, 'valueControlMessage');
    return result;
  }

  /**
   * Clone d'une mesure pour édition
   * @param measure la mesure à cloner
   * @return MeasureVO,  Un clone
   */
  public clone(meas: MeasureVO): MeasureVO {
    const clone = new MeasureVO(cloneDeep(meas.source));
    return clone;
  }

  /**
   * retour les mesures ET les Wgs "encapsulés en mesures "
   * @param visionCode vision cible
   * @param stepLevel niveau de step demandé
   */
  public getMeasureAndWg(visionCode: string, stepLevel: number): MeasureVO[] {
    let resultMap: Map<string, MeasureVO> = new Map<string, MeasureVO>();
    let result: MeasureVO[] = Array.from(
      this.getMeasureList().filter(
        (meas: MeasureVO) => meas.vision === visionCode && meas.stepLevel === 0
      )
    ); // prise des mesures de la vision (sans override)
    resultMap = new Map(result.map((meas: MeasureVO) => [meas.code, meas]));

    // override éventuel selon le stepLevel
    result = Array.from(
      this.getMeasureList().filter(
        (meas: MeasureVO) => meas.vision === visionCode && meas.stepLevel === stepLevel
      )
    ); // prise des mesures de la vision avec override
    result.forEach((meas: MeasureVO) => {
      resultMap.set(meas.code, meas);
    });

    if (
      this.getMeasureMap().has(ViewData.wildcardDomain) &&
      this.getMeasureMap().get(ViewData.wildcardDomain).has(0)
    ) {
      // prise des constantes (à la vision / BU)
      Array.from(this.model.measureEngine.getMeasureMap().get(ViewData.wildcardDomain).get(0).values()).forEach(
        (meas: MeasureVO) => {
          resultMap.set(meas.code, meas);
        }
      );
    }

    this.model.visionMeasures = Array.from(resultMap.values()); // on pousse toutes les mesures de la vision dans le tableau courant

    if (this.model.weightGridEngine.getWeightAsMeasureMap()?.has(this.model.businessUnitCode)) {
      // prise de WGs
      const wgMeas: MeasureVO[] = Array.from(this.model.weightGridEngine.getWeightAsMeasureMap().values());
      wgMeas.forEach((meas: MeasureVO) => {
        resultMap.set(meas.code, meas);
      });
    }
    return Array.from(resultMap.values());
  }

  /**
   * FEI de travail pour la vue des mesures
   */
  public get currentFEI(): FeiVO {
    if (!this.model.currentMeasureFEI) {
      const vision: VisionVO = this.model.currentVision;
      const step: VisionStep = BaselineEngine.getVisionStepByLevel(vision, this.model.currentStepLevel > 0 ? this.model.currentStepLevel : 1);
      const fei: FormulaEngineInstance = new FormulaEngineInstance();
      fei.baselines = {};
      fei.vision = vision.source;
      fei.visionStep = step;
      this.model.currentMeasureFEI = new FeiVO(fei, this.model, vision, step, ViewData.DEFAULT_SCENARIO);
    }

    return this.model.currentMeasureFEI;
  }

  /** rincer le FEI de simulation */
  public clearFEI(): void {
    this.model.currentMeasureFEI = null;
  }

  /**
   * alimentation de la DS en données
   *
   * @param visionCode vision cible
   * @param stepLevel niveau de step demandé
   */
  public buildDS(visionCode: string, stepLevel: number): void {
    this.model.measureDS.setSource(this.getMeasureAndWg(visionCode, stepLevel)); // on ajoute les WGs encapsulées en MeasureVO
  }


  /**
   * pour une measure#mode donnée, retourne son MeasMode
   * @param mes: MeasureVO
   * @return le MM
   */
  public getMeasureMode(fei: FeiVO, mes: MeasureVO): MeasMode {
    let mode: string = this.model.currentFistMode;
    if (mes.mode === WizConstants.MEASURE_MODE_CONSTANT) {
      mode = WizConstants.MEASURE_MODE_CONSTANT;
    } else if (mes.mode === WizConstants.MEASURE_MODE_WEIGHT_GRID) {
      mode = WizConstants.MEASURE_MODE_WEIGHT_GRID;
    }
    const key: string = mes.shortCode + WizConstants.CONTEXT_SEPARATOR + mode;

    let result: MeasMode = null;
    if (fei.matrix && fei.matrix.has(key)) {
      result = fei.matrix.get(key);
    }
    return result;
  }

  /**
   * pour une mesure donnée, affichage de la pile de dépendances.
   * @param e la mesure cible
   */
  public showDependency(fei: FeiVO, e: MeasureVO): void {
    const upds: MeasureVO[] = [];
    upds.push(e);
    // e.filterSelected = true;
    const mm: MeasMode = this.getMeasureMode(fei, e);
    // console.log('mm', mm, e.isConstant(), e.isWeight());
    if (mm.deps) {
      [...mm.deps.values()].forEach((key: string) => {
        const mode: string = key.split(ViewData.CONTEXT_SEPARATOR)[1];
        const code: string = key.split(ViewData.CONTEXT_SEPARATOR)[0];
        const depMeasure: MeasureVO = this.model.measureEngine.getMeasure(e.vision, e.stepLevel, code);
        upds.push(depMeasure);
        // depMeasure.filterSelected = true;
        // console.log('mmDep', depMeasure);
      });
    }
    this.model.measureDS.addSelectedItems(upds);
  }

  /**
   * pour une mesure donnée, affichage de la pile d'héritage.
   * @param e la mesure cible
   */
  public showLegacy(fei: FeiVO, e: MeasureVO): void {
    const upds: MeasureVO[] = [];
    upds.push(e);
    // e.filterSelected = true;
    const mm: MeasMode = this.getMeasureMode(fei, e);
    // console.log('mm', mm, e.isConstant(), e.isWeight());
    if (mm.legs) {
      [...mm.legs.values()].forEach((depCode: string) => {
        const depMeasure: MeasureVO = this.model.measureEngine.getMeasure(
          e.vision,
          e.stepLevel,
          depCode.split(ViewData.CONTEXT_SEPARATOR)[0]
        );
        // depMeasure.filterSelected = true;
        upds.push(depMeasure);
        // console.log('mmLeg', depMeasure);
      });
    }
    this.model.measureDS.addSelectedItems(upds);
  }

  /**
   * retourne true si la mesure est actuellement sélectionnée dans le panel de sélection de la vue mesure
   * @param m la mesureVO
   * @returns 
   */
  public getMeasureIsSelected(m: MeasureVO): boolean { 
    
    if (this.model.measureDS?.selectedItems?.length > 0) {
      let result: boolean = this.model.measureDS?.selectedItems.filter(item => item.code === m.code).length > 0;
      return result;
    }
    return false;
  }

  /**
   * retourne true si la mesure ne dépend d'aucun calcul
   * @param m la mesureVO
   * @returns 
   */ 
  public getMeasureNoDependency(m: MeasureVO): boolean {
    if (
      m.mode === WizConstants.MEASURE_MODE_CONSTANT ||
      m.mode === WizConstants.MEASURE_MODE_WEIGHT_GRID
    ) {
      return true; // pas de dépendances pour les constantes et les grilles de poids
    } 

    const key: string = m.code + WizConstants.CONTEXT_SEPARATOR + this.model.currentFistMode;
    let fei: FeiVO = this.currentFEI;
    let mm: MeasMode = fei.matrix?.get(key);
    if(mm?.deps?.size > 0) {
      return false;
    }
      return true;
    }

   /**
   * create/ update d'une mesure
   * @param to le clone en cours de modif
   * @param from l'objet d'origine, inaltéré
   * @param callback retour dans la vue
   */
   public merge(to: MeasureVO, from: MeasureVO, callback: Function): void {
    let pack: EntityModificationPack;
    if (to.id > 0) {
      pack = EMEngine.mergeEM(to, from);
    } else {
      pack = EMEngine.createEM(to);
    }

    if (pack && pack.ems.length > 0) {
      // a minima une modif, sinon, c'est le même objet (update)
      new CommitCmd(this.model).commit(pack, this.backFromCall.bind(this), [to], callback).execute();
    } else {
      // modèle, le clone est une copie de la valeur d'origine
      ViewData.wizUpdateModel = false;
      callback(to, WIZ_COMMAND_STATE.SUCCESS); // retour en vue
    }
  }

  /**
   * delete d'une mesure
   * @param to le clone à supprimer
   * @param callback retour dans la vue
   */
  public remove(to: MeasureVO, callback: Function): void {
    const pack: EntityModificationPack = EMEngine.removeEM(to);
    new CommitCmd(this.model).commit(pack, this.backFromCall.bind(this), [to], callback).execute();
  }

  /**
   * gestion unifiée des retours de service CUD
   * @param cmd la commande commit qui porte les EMs et les objets
   */
  private backFromCall(cmd: CommitCmd): void {
    // modèle OK (rollback en cas d'erreur)
    ViewData.wizUpdateModel = false;

    // erreur physique
    if (cmd.state === WIZ_COMMAND_STATE.FAULT) {
      WizPopUp.toast('$.common.toast.service.error.$', WizIcon.TOAST_ERROR).show();
    } else {
      // le VO source
      const vo: MeasureVO = cmd.sourceVO[0] as MeasureVO;
      // une seule modification à la fois, packEntry donne l'objet
      const source: any = cmd.result.entries[0].entity;
      // l'action (un seul type)
      const operation: string = cmd.result.ems[0].operation;
      // la liste d'erreurs métier
      const errors: BusinessException[] = cmd.result.errors;
      // le statut du pack
      const packStatus: string = cmd.result.status;

      if (packStatus === WIZ_COMMAND_STATE.FAULT) {
        errors.forEach((be: BusinessException) => {
          WizPopUp.toast(be.message, WizIcon.TOAST_ERROR).show();
        });
      } else {
        switch (operation) {
          case EM_ACTION.UPDATE:
            vo.source = source; // le clone
            const sourceMeasure: MeasureVO = this.model.measureEngine.getMeasure(
              vo.vision,
              vo.stepLevel,
              vo.shortCode
            );
            if (sourceMeasure) {
              sourceMeasure.source = source ;
            }
            break;
          case EM_ACTION.CREATE:
            vo.source = source;
            this.model.measureEngine.getMeasureList().push(vo); // ajout au modèle
            MeasureCoreEngine.addMeasureToMap(this.model.measureEngine.getMeasureMap(), vo);

              if (vo.stepLevel > 0) {
                // on crée un override, suppression de la mesure "vision"
                this.model.measureDS.removeItem(
                  this.model.measureDS.getItemByKey(vo.code)
                );
              }
              this.model.measureDS.addItem(vo); // rebuild de la datasource pour ajout de l'item
              this.model.visionMeasures.push(vo); // ajout a la collection de travail
              this.model.measureDS.addSelectedItems([this.model.measureDS.getItemByKey(vo.code)]);
              this.model.currentMeasure = vo;

            break;
          case EM_ACTION.DELETE:
            MeasureCoreEngine.removeMeasureFromMap(this.model.measureEngine.getMeasureMap(), vo); // suppression de la map de mesures du modèle
            
            let idx: number = -1;
            this.model.measureEngine.getMeasureList().forEach((v, i) => {
              if(v.id === vo.id) {
                idx = i;
                return;
                // console.log('idx measureList', idx);
              }
            })
            const toDie: MeasureVO = this.model.measureEngine.getMeasureList().splice(idx, 1)[0];

            let idx2: number = -1;
            this.model.visionMeasures.forEach((v, i) => {
              if(v.id === vo.id) {
                idx2 = i;
                return;
                // console.log('idx2 vision', idx2);
              }
            })
            const dead = this.model.visionMeasures.splice(idx2, 1); // suppression la collection de travail
            this.model.measureDS.removeItem(toDie); // rebuild de la datasource pour suppression de l'item
            
            break;
        }
      }
      cmd.viewCallBack(vo, packStatus); // retour en vue
    }
  }
   
  /**
   * retour true s'il existe au moins une dépendance pour la mesure dans le mode de calcul actuel
   * @param o measureVO à tester
   * @returns boolean
   */
  public hasDependencies(fei: FeiVO, o: MeasureVO): boolean {
    if (o) {
      const mm: MeasMode = this.getMeasureMode(fei, o);
      if (mm?.deps?.size > 0) {
        return true;
      }
    }
    return false;
  }

  /**
   * retour true s'il existe au moins un héritage pour la mesure dans le mode de calcul actuel
   * @param o measureVO à tester
   * @returns boolean
   */
  public hasLegacies(fei: FeiVO, o: MeasureVO): boolean {
    if (o) {
      const mm: MeasMode = this.getMeasureMode(fei, o);
      if (mm?.legs?.size > 0) {
        return true;
      }
    }
    return false;
  }

  /**
   * retour true s'il existe au moins une dépendance NON AFFICHEE dans la matrice pour la mesure dans le mode de calcul actuel
   * @param o measureVO à tester
   * @returns boolean
   */
  public hasHiddenDependencies(fei: FeiVO, o: MeasureVO): boolean {
    let result: boolean = false;
    if (o) {
      const mm: MeasMode = this.getMeasureMode(fei, o);
      if (!!mm?.deps) {
        [...mm.deps.values()].forEach((key: string) => {
          const mode: string = key.split(ViewData.CONTEXT_SEPARATOR)[1];
          const code: string = key.split(ViewData.CONTEXT_SEPARATOR)[0];
          const depMeasure: MeasureVO = this.getMeasure(o.vision, o.stepLevel, code);
          if (!this.model.measureDS.isSelected(depMeasure)) {
            result = true;
          }
        });
      }
    }
    return result;
  }

  /**
   * retour true s'il existe au moins un héritage NON AFFICHE dans la matrice pour la mesure dans le mode de calcul actuel
   * @param o measureVO à tester
   * @returns boolean
   */
  public hasHiddenLegacies(fei: FeiVO, o: MeasureVO): boolean {
    let result: boolean = false;
    if (o) {
      const mm: MeasMode = this.getMeasureMode(fei, o);
      if (mm?.legs) {
        [...mm.legs.values()].forEach((key: string) => {
          const mode: string = key.split(ViewData.CONTEXT_SEPARATOR)[1];
          const code: string = key.split(ViewData.CONTEXT_SEPARATOR)[0];
          const depMeasure: MeasureVO = this.getMeasure(o.vision, o.stepLevel, code);
          if (!this.model.measureDS.isSelected(depMeasure)) {
            result = true;
          }
        });
      }
    }
    return result;
  }

  /** affichage du petit crayon d'édition, si éditable ET mode d'affichage des measures = I */
  public isReallyEditable(data: MeasureVO): boolean {
    if (data.editable && this.model.currentFistMode === WizConstants.MEASURE_MODE_INTERACT) {
      return true;
    }
    return false;
  }

 /**
  * retourne la formule selon le mode en cours de sélection dans MeasureManager
  * retourne toujours S pour les constantes
  * 
  * @param m la mesureVO
  * @returns 
  */
 public getMeasureCurrentFormulaByMode(m: MeasureVO): string {
  if (m.isConstant || m.isWeight) { // pour les constantes, toujours S // idem pour les weightGrids
    return m.formulaS;
  } else {
    return m['formula' + this.model.currentFistMode];
  }
}

  /** 
    * retourne toutes les mesures similaire, avec overrides par step
    * (une seule, sans step, si pas d'override)
    */
  public getSimilarMeasures(measure: MeasureVO): MeasureVO[] {
    let meas: MeasureVO[] = this.getMeasureList().filter((m) => m.code === measure.code && m.vision === measure.vision)
      .sort((a, b) => a.stepLevel - b.stepLevel);
    if (!meas) {
      meas = [];
    }
    return meas;
  }
 
 
   /**
    * cherche un doublon sur le code en cours de création
    */
   public duplicatedCode(meas: MeasureVO): boolean {
     let result: boolean = false;
     const other: MeasureVO = this.getMeasure(meas.vision, meas.stepLevel, meas.code);
     if (other && other.uid !== meas.uid) {
       // un autre avec un UID différent (un vrai autre, pas moi)
       result = true;
     }
     return result;
   }
 
   /**
    * contrôle la cohérence de la formule passée en paramètre
    * @param meas la mesure à tester
    * @param mode le mode FIST
    * @return le couple [erreur ?, code de l'erreur]
    */
   public isValidFormula(fei: FeiVO, meas: MeasureVO, mode: string): [boolean, string] {
     const mm: MeasMode = MeasureCoreEngine.measureModeFromFormula(fei, meas.source, mode);
     return [mm.isErr, mm.errC];
   }
 
   /**
    * Véification que l'object measure est valide (code, businessUnitCode, domain & formules cohérentes)
    * @param meas MeasureVO
    * @return boolean true si la mesure est valide, false sinon
    */
   public isValid(fei, meas: MeasureVO): boolean {
     if (!meas) {
       return false;
     }
 
     if (meas.code === undefined || meas.code === null) {
       return false;
     }
     if (meas.domain === undefined || meas.domain === null) {
       return false;
     }
     if (meas.businessUnitCode === undefined || meas.businessUnitCode === null) {
       return false;
     }
 
     let mm: MeasMode;
     if (meas.formulaF) {
       // contrôle des formules
       mm = MeasureCoreEngine.measureModeFromFormula(fei, meas.source, WizConstants.MEASURE_MODE_FROM);
       if (mm.isErr) {
         return false;
       }
     }
     if (meas.formulaI) {
       // contrôle des formules
       mm = MeasureCoreEngine.measureModeFromFormula(fei, meas.source, WizConstants.MEASURE_MODE_INTERACT);
       if (mm.isErr) {
         return false;
       }
     }
     if (meas.formulaS) {
       // contrôle des formules
       mm = MeasureCoreEngine.measureModeFromFormula(fei, meas.source, WizConstants.MEASURE_MODE_SUBMIT);
       if (mm.isErr) {
         return false;
       }
     }
     if (meas.formulaT) {
       // contrôle des formules
       mm = MeasureCoreEngine.measureModeFromFormula(fei, meas.source, WizConstants.MEASURE_MODE_TO);
       if (mm.isErr) {
         return false;
       }
     }
     return true;
   }
 
   /**
    * Vérifie si une mesure est supprimable (pas utilisée)
    * TODO même traitement sur update ?
    * @param meas La mesure à supprimer
    * @return boolean true si la mesure est supprimable (n'est pas utilisée), false sinon
    */
   public isValidForRemove(meas: MeasureVO): boolean {
     return true; // TODO
   }
 


}
